import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import StrainReel from '../components/StrainReel';

const DEFAULT_GRAM_OPTIONS = [
  { amount: '1g', points: 50, spins: 0, grams: 1 },
  { amount: '2g', points: 100, spins: 1, grams: 2 },
  { amount: '3.5g', points: 175, spins: 2, grams: 3.5 },
  { amount: '7g', points: 350, spins: 3, grams: 7 },
  { amount: '14g', points: 700, spins: 5, grams: 14 },
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const AMOUNT_PRICE_KEY = {
  '1g': 'price_1g',
  '2g': 'price_2g',
  '3.5g': 'price_3_5g',
  '7g': 'price_7g',
  '14g': 'price_14g',
};

export default function BookingsPage() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [strainId, setStrainId] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [cart, setCart] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [hours, setHours] = useState([]);
  const [hoursLoaded, setHoursLoaded] = useState(false);
  const [strains, setStrains] = useState([]);
  const [gramOptions, setGramOptions] = useState(DEFAULT_GRAM_OPTIONS);
  const [popularStrain, setPopularStrain] = useState(null);
  const [reelEnabled, setReelEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [error, setError] = useState('');
  const [hoveredStrainId, setHoveredStrainId] = useState(null);

  const fetchStrains = () => {
    api.get('/strains').then(setStrains).catch(() => {});
  };

  useEffect(() => {
    api.get('/bookings').then(setBookings).catch(() => {});
    api.get('/opening-hours').then(data => { setHours(data); setHoursLoaded(true); }).catch(() => { setHoursLoaded(true); });
    fetchStrains();
    api.get('/strains/popular').then(data => {
      if (data) setPopularStrain(data);
    }).catch(() => {});
    api.get('/strains/settings').then(data => {
      if (data) setReelEnabled(data.enable_strain_reel);
    }).catch(() => {});
    // Fetch dynamic rates
    api.get('/points/rates').then(data => {
      if (data && data.points) {
        const opts = Object.entries(data.points)
          .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
          .map(([g, pts]) => ({
            amount: g + 'g',
            points: pts,
            spins: data.spins?.[g] || 0,
            grams: parseFloat(g),
          }));
        if (opts.length > 0) setGramOptions(opts);
      }
    }).catch(() => {});
  }, []);

  const selected = gramOptions.find(o => o.amount === amount);
  const selectedStrain = strains.find(s => s.id === strainId);
  const insufficientStock = selectedStrain && selected && selectedStrain.stock_grams < selected.grams;
  const lowStock = selectedStrain && selected && !insufficientStock && selectedStrain.stock_grams < selected.grams + 10;

  // Calculate tiered rewards for the whole cart (mix-and-match)
  const cartTotalGrams = cart.reduce((sum, item) => {
    const opt = gramOptions.find(o => o.amount === item.amount);
    return sum + (opt ? opt.grams : 0);
  }, 0);

  const cartTiers = (() => {
    const tiers = [14, 7, 3.5, 2, 1];
    const result = [];
    let remaining = cartTotalGrams;
    for (const tier of tiers) {
      while (remaining >= tier) {
        result.push(tier);
        remaining -= tier;
      }
    }
    return result;
  })();

  // Build a lookup from gramOptions for tier-based points/spins
  const gramPointsMap = {};
  const gramSpinsMap = {};
  gramOptions.forEach(o => { gramPointsMap[o.grams] = o.points; gramSpinsMap[o.grams] = o.spins; });

  const cartTieredPoints = cartTiers.reduce((sum, t) => sum + (gramPointsMap[t] || 0), 0);
  const cartTieredSpins = cartTiers.reduce((sum, t) => sum + (gramSpinsMap[t] || 0), 0);
  const cartTierDesc = cartTiers.map(t => `${t}g`).join(' + ');

  // Per-item totals (for comparison / single-item display)
  const cartPerItemPoints = cart.reduce((sum, item) => {
    const opt = gramOptions.find(o => o.amount === item.amount);
    return sum + (opt ? opt.points : 0);
  }, 0);
  const cartPerItemSpins = cart.reduce((sum, item) => {
    const opt = gramOptions.find(o => o.amount === item.amount);
    return sum + (opt ? opt.spins : 0);
  }, 0);

  const isBulk = cart.length > 1;
  const cartTotalPoints = isBulk ? cartTieredPoints : cartPerItemPoints;
  const cartTotalSpins = isBulk ? cartTieredSpins : cartPerItemSpins;

  const getHoursForDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr + 'T12:00:00');
    const day = date.getDay();
    return hours.find(h => h.day_of_week === day);
  };

  const selectedDayHours = getHoursForDate(pickupDate);

  const isClosedDay = (dateStr) => {
    const dayHours = getHoursForDate(dateStr);
    return dayHours && !dayHours.is_open;
  };

  // Returns the next open date (YYYY-MM-DD) on or after fromDateStr
  const getNextOpenDate = (fromDateStr) => {
    const base = fromDateStr ? new Date(fromDateStr + 'T12:00:00') : new Date();
    for (let i = 0; i <= 14; i++) {
      const d = new Date(base);
      d.setDate(d.getDate() + i);
      const dh = hours.find(h => Number(h.day_of_week) === d.getDay());
      if (dh?.is_open) return d.toISOString().split('T')[0];
    }
    return fromDateStr;
  };

  // Returns 'HH:MM' string for next open slot starting today (used for the closed banner)
  const getNextOpenInfo = () => {
    const now = new Date();
    const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const fmtT = (t) => {
      const [h, m] = t.split(':').map(Number);
      if (h === 12 && m === 0) return 'midday';
      if (h === 0 && m === 0) return 'midnight';
      const hr = h % 12 || 12;
      const ap = h < 12 ? 'am' : 'pm';
      return m === 0 ? `${hr}${ap}` : `${hr}:${String(m).padStart(2, '0')}${ap}`;
    };
    for (let i = 0; i <= 7; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() + i);
      const idx = d.getDay();
      const dh = hours.find(h => Number(h.day_of_week) === idx);
      if (!dh?.is_open || !dh.open_time) continue;
      if (i === 0) {
        const [oh, om] = dh.open_time.split(':').map(Number);
        if (now.getHours() * 60 + now.getMinutes() < oh * 60 + om) {
          return { label: `Today at ${fmtT(dh.open_time)}`, date: d.toISOString().split('T')[0], open_time: dh.open_time };
        }
        continue;
      }
      const dayLabel = i === 1 ? 'Tomorrow' : DAY_SHORT[idx];
      return { label: `${dayLabel} at ${fmtT(dh.open_time)}`, date: d.toISOString().split('T')[0], open_time: dh.open_time };
    }
    return null;
  };

  const handleDateChange = (val) => {
    if (!val) { setPickupDate(''); return; }
    if (isClosedDay(val)) {
      const next = getNextOpenDate(val);
      setPickupDate(next);
      const dh = getHoursForDate(next);
      if (dh?.open_time) setPickupTime(dh.open_time.substring(0, 5));
    } else {
      setPickupDate(val);
    }
  };

  const handleTimeChange = (val) => {
    if (!val || !selectedDayHours?.is_open) { setPickupTime(val); return; }
    const [h, m] = val.split(':').map(Number);
    const mins = h * 60 + m;
    const [oh, om] = (selectedDayHours.open_time || '00:00').split(':').map(Number);
    const [ch, cm] = (selectedDayHours.close_time || '23:59').split(':').map(Number);
    const openMins = oh * 60 + om;
    const closeMins = ch * 60 + cm;
    if (mins < openMins) {
      setPickupTime(selectedDayHours.open_time.substring(0, 5));
    } else if (closeMins > 0 && mins > closeMins) {
      setPickupTime(selectedDayHours.close_time.substring(0, 5));
    } else {
      setPickupTime(val);
    }
  };

  const isCurrentlyOpen = (() => {
    if (!hoursLoaded) return null; // still fetching — render nothing
    const now = new Date();
    const todayHours = hours.find(h => Number(h.day_of_week) === now.getDay());
    if (!todayHours?.is_open) return false;
    if (!todayHours.open_time || !todayHours.close_time) return true;
    const [oh, om] = todayHours.open_time.split(':').map(Number);
    const [ch, cm] = todayHours.close_time.split(':').map(Number);
    const openMin = oh * 60 + om;
    const closeMin = ch * 60 + cm;
    const nowMins = now.getHours() * 60 + now.getMinutes();
    // Midnight or overnight close (e.g. 17:30–00:00): open if past open time
    if (closeMin === 0 || closeMin <= openMin) {
      return nowMins >= openMin || (closeMin > 0 && nowMins < closeMin);
    }
    return nowMins >= openMin && nowMins < closeMin;
  })();

  const BASKET_LIMIT = 500;
  const cartTotalPrice = cart.reduce((sum, i) => sum + (i.price || 0), 0);
  const selectedPrice = (() => {
    if (!selectedStrain || !amount) return 0;
    const priceKey = AMOUNT_PRICE_KEY[amount];
    return priceKey ? parseFloat(selectedStrain[priceKey]) || 0 : 0;
  })();
  const wouldExceedLimit = cartTotalPrice + selectedPrice > BASKET_LIMIT;
  const addReady = strainId && amount && !insufficientStock && !wouldExceedLimit;

  const getLowestPrice = (strain) => {
    const prices = ['price_1g', 'price_2g', 'price_3_5g', 'price_7g', 'price_14g']
      .map(k => parseFloat(strain[k]) || 0)
      .filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  const TYPE_STYLE = {
    indica:  { bg: 'rgba(107,70,193,0.2)',  text: '#d6bcfa', label: 'Indica' },
    sativa:  { bg: 'rgba(237,137,54,0.2)',  text: '#f6ad55', label: 'Sativa' },
    hybrid:  { bg: 'rgba(56,161,105,0.2)',  text: '#48bb78', label: 'Hybrid' },
  };

  const handleAddToCart = () => {
    if (!amount || !strainId) return;
    const strain = strains.find(s => s.id === strainId);
    const priceKey = AMOUNT_PRICE_KEY[amount];
    const price = strain && priceKey ? parseFloat(strain[priceKey]) || 0 : 0;
    setCart(prev => [...prev, {
      id: Date.now(),
      strain_id: strainId || null,
      strain_name: strain ? strain.name : 'Any strain',
      amount,
      price,
    }]);
    setAmount('');
    setStrainId('');
  };

  const handleRemoveFromCart = (itemId) => {
    setCart(prev => prev.filter(i => i.id !== itemId));
  };

  const submitOrder = async (date, time) => {
    setError('');
    setLoading(true);
    try {
      const pickupDateTime = new Date(`${date}T${time}`);
      await api.post('/bookings/bulk', {
        items: cart.map(i => ({ strain_id: i.strain_id, amount: i.amount })),
        pickupTime: pickupDateTime.toISOString(),
      });
      setCart([]);
      setPickupDate('');
      setPickupTime('');
      navigate('/', { state: { newOrder: true } });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) { setError('Add at least one item to your order'); return; }
    if (!pickupDate || !pickupTime) { setError('Pick a date and time'); return; }
    if (isClosedDay(pickupDate)) { setError('The club is closed that day'); return; }
    await submitOrder(pickupDate, pickupTime);
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this item?')) return;
    setCancelling(id);
    try {
      await api.put(`/bookings/${id}/cancel`);
      const updated = await api.get('/bookings');
      setBookings(updated);
    } catch {
      // ignore
    } finally {
      setCancelling(null);
    }
  };

  // When hours load and shop is closed, pre-fill date/time to next open slot
  useEffect(() => {
    if (!hoursLoaded || isCurrentlyOpen !== false || pickupDate) return;
    const next = getNextOpenInfo();
    if (next) {
      setPickupDate(next.date);
      setPickupTime(next.open_time.substring(0, 5));
    }
  }, [hoursLoaded, isCurrentlyOpen]); // eslint-disable-line

  const today = new Date();
  const minDate = today.toISOString().split('T')[0];

  // Group bookings by batch_id for display
  const groupedBookings = [];
  const seen = new Set();
  bookings.forEach(b => {
    if (b.batch_id && !seen.has(b.batch_id)) {
      seen.add(b.batch_id);
      groupedBookings.push({
        batch_id: b.batch_id,
        pickup_time: b.pickup_time,
        items: bookings.filter(x => x.batch_id === b.batch_id),
      });
    } else if (!b.batch_id) {
      groupedBookings.push({
        batch_id: null,
        pickup_time: b.pickup_time,
        items: [b],
      });
    }
  });

  const todayDow = today.getDay();

  const formatPickupTime = (isoString) => {
    const dt = new Date(isoString);
    const now = new Date();
    const isToday = dt.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = dt.toDateString() === tomorrow.toDateString();
    const hrs = dt.getHours();
    const m = dt.getMinutes();
    let time;
    if (hrs === 12 && m === 0) time = 'midday';
    else if (hrs === 0 && m === 0) time = 'midnight';
    else {
      const h = hrs % 12 || 12;
      const ap = hrs < 12 ? 'am' : 'pm';
      time = m === 0 ? `${h}${ap}` : `${h}:${String(m).padStart(2, '0')}${ap}`;
    }
    if (isToday) return `Today at ${time}`;
    if (isTomorrow) return `Tomorrow at ${time}`;
    const DAY_S = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const MON_S = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${DAY_S[dt.getDay()]} ${dt.getDate()} ${MON_S[dt.getMonth()]} at ${time}`;
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
        <h1 style={{ marginBottom: 0 }}>Pre-Order Pickup</h1>
        {popularStrain && (
          <div className="popular-strain-badge" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
            padding: '6px 12px', borderRadius: 'var(--radius-sm)',
            background: 'linear-gradient(135deg, rgba(214,158,46,0.15), rgba(214,158,46,0.05))',
            border: '1px solid rgba(214,158,46,0.4)',
          }}>
            <span style={{ fontSize: 15 }}>&#127942;</span>
            <span style={{ fontSize: 12, color: '#ecc94b', fontWeight: 600 }}>
              {popularStrain.name}
            </span>
          </div>
        )}
      </div>
      <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
        Build your order and earn Bud Points when you scoop
      </p>

      {reelEnabled && strains.length > 0 && (
        <StrainReel strains={strains} />
      )}

      {strains.length === 0 ? (
        <div className="card" style={{ marginBottom: 24, maxWidth: 500, textAlign: 'center', padding: '32px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>&#128683;</div>
          <h3 style={{ margin: '0 0 8px', color: 'var(--danger)' }}>Shop's Closed</h3>
          <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: 14 }}>
            No stock available right now — check back soon!
          </p>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 24, maxWidth: 500 }}>
          {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

          {/* Item picker */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Strain</label>
              {!strainId && <span className="tap-hint-pulse" style={{ fontSize: 11, color: 'var(--accent)', fontStyle: 'italic', fontWeight: 600 }}>tap to select</span>}
            </div>
            <div className="strain-picker-grid">
              {strains.map(s => {
                const isSelected = s.id === strainId;
                const isHovered = s.id === hoveredStrainId && !isSelected;
                const typeKey = s.type?.toLowerCase();
                const typeStyle = TYPE_STYLE[typeKey];
                const lowestPrice = getLowestPrice(s);
                const isLowStock = s.stock_grams < 10;
                return (
                  <div
                    key={s.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setStrainId(isSelected ? '' : s.id)}
                    onKeyDown={e => e.key === 'Enter' && setStrainId(isSelected ? '' : s.id)}
                    onMouseEnter={() => setHoveredStrainId(s.id)}
                    onMouseLeave={() => setHoveredStrainId(null)}
                    style={{
                      position: 'relative',
                      padding: '12px 14px',
                      borderRadius: 'var(--radius-sm)',
                      border: isSelected
                        ? '2px solid var(--accent)'
                        : isHovered
                          ? '2px solid rgba(56,161,105,0.5)'
                          : '2px solid var(--border)',
                      background: isSelected
                        ? 'rgba(56,161,105,0.12)'
                        : isHovered
                          ? 'rgba(56,161,105,0.05)'
                          : 'var(--bg-secondary)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
                      boxShadow: isSelected
                        ? '0 0 0 3px rgba(56,161,105,0.15)'
                        : isHovered
                          ? '0 2px 8px rgba(0,0,0,0.15)'
                          : 'none',
                      outline: 'none',
                    }}
                  >
                    {isSelected && (
                      <span style={{
                        position: 'absolute', top: 6, right: 8,
                        width: 18, height: 18, borderRadius: '50%',
                        background: 'var(--accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, color: '#fff', fontWeight: 700, lineHeight: 1,
                      }}>✓</span>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4, paddingRight: isSelected ? 20 : 0 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: isSelected ? 'var(--accent)' : 'var(--text-primary)', lineHeight: 1.2 }}>{s.name}</span>
                      {typeStyle && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 6px',
                          borderRadius: 20, background: typeStyle.bg, color: typeStyle.text,
                        }}>
                          {typeStyle.label}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {lowestPrice !== null && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>from £{lowestPrice}</span>
                      )}
                      {isLowStock && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#f6ad55' }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f6ad55', display: 'inline-block' }} />
                          low stock
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {insufficientStock && (
              <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>
                Not enough stock available for this amount
              </div>
            )}
            {lowStock && (
              <div style={{ color: 'var(--warning)', fontSize: 12, marginBottom: 8 }}>
                Stock is running low on this strain
              </div>
            )}

            <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, fontWeight: 500 }}>Weight</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {gramOptions.map(o => {
                const isSelected = o.amount === amount;
                const priceKey = AMOUNT_PRICE_KEY[o.amount];
                const price = selectedStrain && priceKey ? parseFloat(selectedStrain[priceKey]) || 0 : 0;
                return (
                  <div
                    key={o.amount}
                    onClick={() => setAmount(isSelected ? '' : o.amount)}
                    style={{
                      minWidth: 72, padding: '8px 12px',
                      borderRadius: 'var(--radius-sm)',
                      border: isSelected ? 'none' : '1px solid var(--border)',
                      background: isSelected ? 'var(--accent)' : 'var(--bg-secondary)',
                      color: isSelected ? '#fff' : 'var(--text-primary)',
                      cursor: 'pointer',
                      textAlign: 'center',
                      userSelect: 'none',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                      {o.amount}
                      {price > 0 && selectedStrain && (
                        <span style={{ fontWeight: 400, fontSize: 13, color: isSelected ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)', marginLeft: 4 }}>
                          · £{price}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: isSelected ? 'rgba(255,255,255,0.75)' : 'var(--text-muted)', marginTop: 2 }}>
                      +{o.points} pts{o.spins > 0 ? ` · +${o.spins} spin${o.spins > 1 ? 's' : ''}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>

            {wouldExceedLimit && strainId && amount && (
              <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>
                Basket limit is £{BASKET_LIMIT} — remove an item to add this one
              </div>
            )}

            <button
              type="button"
              className={addReady ? 'btn-primary add-to-order-glow' : 'btn-secondary'}
              disabled={!strainId || !amount || insufficientStock || wouldExceedLimit}
              onClick={handleAddToCart}
              style={{ width: '100%' }}
            >
              + Add to Order
            </button>
          </div>

          {/* Cart */}
          {cart.length > 0 && (
            <div style={{
              background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
              padding: 12, marginBottom: 16
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Your Order ({cart.length} item{cart.length > 1 ? 's' : ''})
              </div>
              {cart.map(item => {
                const opt = gramOptions.find(o => o.amount === item.amount);
                return (
                  <div key={item.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <div>
                        <span style={{ fontWeight: 600 }}>{item.strain_name}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: 13, marginLeft: 6 }}>{item.amount}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {item.price > 0 && (
                          <span style={{ color: 'var(--accent)', fontSize: 16, fontWeight: 700 }}>
                            &pound;{item.price.toFixed(2)}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveFromCart(item.id)}
                          style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      +{opt?.points} pts{opt?.spins > 0 ? ` · +${opt.spins} spin${opt.spins > 1 ? 's' : ''}` : ''}
                    </div>
                  </div>
                );
              })}
              {(() => {
                return (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                    {cartTotalPrice > 0 && (
                      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>
                        &pound;{cartTotalPrice.toFixed(2)}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ background: 'rgba(72,187,120,0.15)', color: 'var(--success)', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                        +{cartTotalPoints} pts
                      </span>
                      {cartTotalSpins > 0 && (
                        <span style={{ background: 'rgba(214,188,250,0.15)', color: '#d6bcfa', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                          +{cartTotalSpins} spin{cartTotalSpins > 1 ? 's' : ''}
                        </span>
                      )}
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>on pickup</span>
                    </div>
                    {isBulk && cartTieredPoints > cartPerItemPoints && (
                      <div style={{ marginTop: 6 }}>
                        <span style={{ background: 'rgba(72,187,120,0.12)', color: 'var(--success)', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                          Bulk bonus applied
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Pickup date/time + submit */}
          <form onSubmit={handleSubmit}>
            {/* ASAP Button — only shown during opening hours */}
            {isCurrentlyOpen === null ? null : isCurrentlyOpen ? (
              <button
                type="button"
                disabled={loading}
                onClick={async () => {
                  const now = new Date();
                  const date = now.toISOString().split('T')[0];
                  const hh = String(now.getHours()).padStart(2, '0');
                  const mm = String(now.getMinutes()).padStart(2, '0');
                  setPickupDate(date);
                  setPickupTime(`${hh}:${mm}`);
                  if (cart.length > 0) await submitOrder(date, `${hh}:${mm}`);
                }}
                style={{
                  width: '100%', padding: '12px', marginBottom: 10,
                  background: 'var(--success)', color: '#fff', border: 'none',
                  borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  fontWeight: 700, fontSize: 15
                }}
              >
                {loading ? 'Locking it in...' : cart.length > 0 ? 'ASAP — Reserve Now' : 'ASAP — Picking up now'}
              </button>
            ) : (
              (() => {
                const next = getNextOpenInfo();
                return (
                  <div style={{
                    width: '100%', padding: '14px 16px', marginBottom: 10,
                    background: 'rgba(237,137,54,0.08)',
                    border: '1px solid rgba(237,137,54,0.35)',
                    borderRadius: 'var(--radius-sm)', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 18, marginBottom: 6 }}>🕐</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}>
                      We're closed right now
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      You can still pre-order — we'll have it ready for collection when we open
                      {next && (
                        <span style={{ display: 'block', marginTop: 4, color: '#ed8936', fontWeight: 700, fontSize: 14 }}>
                          {next.label}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()
            )}

            <div style={{
              textAlign: 'center', color: 'var(--text-muted)', fontSize: 12,
              marginBottom: 10, position: 'relative'
            }}>
              <span style={{ background: 'var(--bg-primary)', padding: '0 8px', position: 'relative', zIndex: 1 }}>
                {isCurrentlyOpen ? 'or pick a date & time' : 'schedule your pre-order'}
              </span>
              <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px solid var(--border)' }} />
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, fontWeight: 500 }}>
                Pickup Date
                {hoursLoaded && hours.length > 0 && (
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                    {' '}(open days only)
                  </span>
                )}
              </label>
              <input
                type="date"
                value={pickupDate}
                onChange={e => handleDateChange(e.target.value)}
                min={minDate}
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, fontWeight: 500 }}>
                Pickup Time
                {selectedDayHours?.is_open && selectedDayHours.open_time && (
                  <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                    {' '}({selectedDayHours.open_time.substring(0,5)} – {selectedDayHours.close_time?.substring(0,5)})
                  </span>
                )}
              </label>
              <input
                type="time"
                value={pickupTime}
                onChange={e => handleTimeChange(e.target.value)}
                min={selectedDayHours?.open_time?.substring(0,5)}
                max={selectedDayHours?.close_time?.substring(0,5)}
                required
              />
              {selectedDayHours && !selectedDayHours.is_open && (
                <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>
                  Closed on {DAY_NAMES[new Date(pickupDate + 'T12:00:00').getDay()]}s — date adjusted automatically
                </div>
              )}
            </div>

            <button type="submit" className="btn-primary" disabled={loading || cart.length === 0} style={{ width: '100%' }}>
              {loading ? 'Locking it in...' : `Reserve My Stash (${cart.length} item${cart.length !== 1 ? 's' : ''})`}
            </button>
          </form>
        </div>
      )}

      {/* Opening Hours Display */}
      {hours.length > 0 && (
        <>
          <div style={{ borderTop: '1px solid var(--border)', marginBottom: 24, maxWidth: 500 }} />
          <div className="card" style={{ marginBottom: 24, maxWidth: 500 }}>
            <h3 style={{ marginBottom: 12 }}>Sesh Hours</h3>
            <div style={{ display: 'grid', gap: 2 }}>
              {hours.map(h => {
                const isToday = h.day_of_week === todayDow;
                return (
                  <div key={h.day_of_week} style={{
                    display: 'flex', justifyContent: 'space-between', padding: '6px 8px',
                    borderRadius: 6, fontSize: 14,
                    background: isToday ? 'rgba(56,161,105,0.12)' : 'transparent',
                    border: isToday ? '1px solid rgba(56,161,105,0.25)' : '1px solid transparent',
                  }}>
                    <span style={{ color: isToday ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: isToday ? 700 : 400 }}>
                      {DAY_NAMES[h.day_of_week]}
                    </span>
                    <span style={{ color: h.is_open ? 'var(--success)' : 'var(--danger)', fontWeight: isToday ? 700 : 400 }}>
                      {h.is_open ? `${h.open_time?.substring(0,5)} - ${h.close_time?.substring(0,5)}` : 'Closed'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Booking History */}
      <div className="card" style={{ maxWidth: 500 }}>
        <h3 style={{ marginBottom: 12 }}>Your Orders</h3>
        {groupedBookings.length === 0 ? (
          <div className="empty-state" style={{ padding: '48px 20px' }}>No orders yet — time to re-up!</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {groupedBookings.map((group) => (
              <div key={group.batch_id || group.items[0].id} style={{
                background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
                padding: 12, border: '1px solid var(--border)'
              }}>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                    {formatPickupTime(group.pickup_time)}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                    {group.items.length} item{group.items.length > 1 ? 's' : ''}
                  </div>
                </div>
                {group.items.map(b => (
                  <div key={b.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '7px 0', borderTop: '1px solid var(--border)'
                  }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{b.strain_name || 'Any'}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 13, marginLeft: 8 }}>{b.amount}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span className={`badge ${
                        b.status === 'completed' ? 'badge-success' :
                        b.status === 'cancelled' ? 'badge-redeemed' :
                        b.status === 'on_tick' ? 'badge-legendary' : 'badge-pending'
                      }`}>
                        {b.status === 'on_tick' ? 'Pending Payment' : b.status}
                      </span>
                      {b.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => handleCancel(b.id)}
                          disabled={cancelling === b.id}
                          style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 12, padding: 0, fontWeight: 600 }}
                        >
                          {cancelling === b.id ? '...' : 'Cancel'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
